import { useUser } from '@clerk/clerk-react'
import { Check, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LANGUAGES } from '@/constants/languages'
import { useAuthToken } from '@/hooks/useAuthToken'
import { meService } from '@/services/meService'
import { normalizeToSupportedLanguageCode } from '@/utils/languageCodeUtils'

export default function LanguagePreferences() {
  const { user } = useUser()
  const { i18n, t } = useTranslation()
  const { getToken } = useAuthToken()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState<string>('')

  // Initialize selected language from user metadata or current i18n language
  useEffect(() => {
    if (user) {
      const savedLanguage = user.unsafeMetadata?.preferredLanguage as string | undefined
      const normalized = normalizeToSupportedLanguageCode(savedLanguage || i18n.language)
      setSelectedLanguage(normalized || savedLanguage || i18n.language)
    }
  }, [user, i18n.language])

  const handleLanguageChange = async (languageCode: string) => {
    if (!user) return

    setLoading(true)
    setSuccess(false)

    try {
      // Update user metadata
      await user.update({
        unsafeMetadata: {
          ...user.unsafeMetadata,
          preferredLanguage: languageCode,
        },
      })

      // Emails pick their locale from it. Best-effort.
      try {
        const token = await getToken()
        if (token) {
          await meService.setPreferredLocale(token, languageCode)
        }
      } catch (error) {
        console.error('Failed to save preferred locale:', error)
      }

      // Update i18n language
      await i18n.changeLanguage(languageCode)

      setSelectedLanguage(languageCode)
      setSuccess(true)

      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(false)
      }, 3000)
    } catch (error) {
      console.error('Failed to update language preference:', error)
      // You could add error state here if needed
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='max-w-2xl space-y-6'>
      <div>
        <h2 className='mb-2 text-2xl font-bold text-gray-900'>{t('languagePreferences.title')}</h2>
        <p className='text-sm text-gray-600'>{t('languagePreferences.description')}</p>
      </div>

      {success && (
        <div className='mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-green-800'>
          <Check size={18} />
          <span className='text-sm font-medium'>{t('languagePreferences.successMessage')}</span>
        </div>
      )}

      <div className='space-y-3'>
        {SUPPORTED_LANGUAGES.map((language) => {
          const isSelected = selectedLanguage === language.code
          const isCurrentlyChanging = loading && selectedLanguage !== language.code

          return (
            <button
              type='button'
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              disabled={loading}
              className={`flex w-full items-center justify-between rounded-lg border-2 p-4 transition-all duration-200 ${
                isSelected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white hover:border-gray-300'
              } ${loading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} `}
            >
              <div className='flex flex-col items-start'>
                <span className='font-semibold text-gray-900'>{language.nativeName}</span>
                <span className='text-sm text-gray-600'>{t(language.name)}</span>
              </div>

              <div className='flex items-center gap-2'>
                {isSelected && !loading && (
                  <div className='flex h-6 w-6 items-center justify-center rounded-full bg-purple-600'>
                    <Check size={16} className='text-white' />
                  </div>
                )}
                {loading && isCurrentlyChanging && <Loader2 size={20} className='animate-spin text-purple-600' />}
              </div>
            </button>
          )
        })}
      </div>

      <div className='mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4'>
        <p className='text-xs text-gray-600'>
          <strong>{t('languagePreferences.note')}</strong> {t('languagePreferences.noteDescription')}
        </p>
      </div>
    </div>
  )
}
